import {
  Badge,
  Button,
  createTableColumn,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  Option,
  type TableColumnDefinition,
  Toolbar,
  tokens,
} from "@fluentui/react-components"
import {
  AddRegular,
  MoreHorizontalRegular,
  PlugConnectedRegular,
  SearchRegular,
} from "@fluentui/react-icons"
import type {
  ApiClient,
  ApiClientPage,
  ApiClientStatus,
  CreateClientRequest,
  CreateClientResponse,
  RotateSecretResponse,
  UpdateClientRequest,
} from "@rema/shared/schemas/clients"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useSession } from "../../auth/session"
import { ConfirmDialog } from "../../components/confirm-dialog"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { PageHeader } from "../../components/page-header"
import { api } from "../../lib/api"
import { ClientFormDrawer } from "./client-form-drawer"
import { clientsI18n as i18n } from "./i18n"

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: { minWidth: "240px" },
  statusFilter: { minWidth: "160px" },
  spacer: { marginLeft: "auto" },
  scopes: { display: "flex", gap: tokens.spacingHorizontalXS, flexWrap: "wrap" },
  mono: { wordBreak: "break-all", fontFamily: tokens.fontFamilyMonospace },
})

const QUERY_KEY = ["admin", "clients"] as const
type StatusFilter = ApiClientStatus | "all"
type SecretReveal = { clientId: string; secret: string }

const STATUS_LABEL: Record<ApiClientStatus, string> = {
  active: i18n.status.active,
  disabled: i18n.status.disabled,
}

const fetchClients = (cursor: string | null, q: string, status: StatusFilter) =>
  api.get<ApiClientPage>("/admin/clients", {
    query: {
      cursor: cursor ?? undefined,
      q: q.trim() || undefined,
      status: status === "all" ? undefined : status,
    },
  })

export const ClientsPage = () => {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { hasScope } = useSession()
  const canWrite = hasScope("clients:write")

  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ApiClient | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ApiClient | null>(null)
  const [secret, setSecret] = useState<SecretReveal | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const createMutation = useMutation({
    mutationFn: (input: CreateClientRequest) =>
      api.post<CreateClientResponse>("/admin/clients", input),
    onSuccess: (created) => {
      invalidate()
      setSecret({ clientId: created.client_id, secret: created.client_secret })
    },
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClientRequest }) =>
      api.patch<ApiClient>(`/admin/clients/${id}`, input),
    onSuccess: invalidate,
  })
  const rotateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<RotateSecretResponse>(`/admin/clients/${id}/rotate-secret`),
    onSuccess: (res) => setSecret({ clientId: res.client_id, secret: res.client_secret }),
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/clients/${id}`),
    onSuccess: invalidate,
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const columns = useMemo<TableColumnDefinition<ApiClient>[]>(
    () => [
      createTableColumn<ApiClient>({
        columnId: "name",
        renderHeaderCell: () => i18n.columns.name,
        renderCell: (item) => item.name,
      }),
      createTableColumn<ApiClient>({
        columnId: "clientId",
        renderHeaderCell: () => i18n.columns.clientId,
        renderCell: (item) => <span className={styles.mono}>{item.client_id}</span>,
      }),
      createTableColumn<ApiClient>({
        columnId: "scopes",
        renderHeaderCell: () => i18n.columns.scopes,
        renderCell: (item) =>
          item.scopes.length === 0 ? (
            i18n.noScopes
          ) : (
            <div className={styles.scopes}>
              {item.scopes.map((s) => (
                <Badge key={s} appearance="outline">
                  {s}
                </Badge>
              ))}
            </div>
          ),
      }),
      createTableColumn<ApiClient>({
        columnId: "status",
        renderHeaderCell: () => i18n.columns.status,
        renderCell: (item) => (
          <Badge appearance="filled" color={item.status === "active" ? "success" : "danger"}>
            {STATUS_LABEL[item.status]}
          </Badge>
        ),
      }),
      createTableColumn<ApiClient>({
        columnId: "lastUsed",
        renderHeaderCell: () => i18n.columns.lastUsed,
        renderCell: (item) =>
          item.last_used_at ? new Date(item.last_used_at).toLocaleString() : i18n.never,
      }),
      createTableColumn<ApiClient>({
        columnId: "actions",
        renderHeaderCell: () => "",
        renderCell: (item) =>
          canWrite ? (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  aria-label={i18n.actions.label}
                  icon={<MoreHorizontalRegular />}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setEditing(item)
                      setFormOpen(true)
                    }}
                  >
                    {i18n.actions.edit}
                  </MenuItem>
                  <MenuItem onClick={() => rotateMutation.mutate(item.id)}>
                    {i18n.actions.rotate}
                  </MenuItem>
                  <MenuItem onClick={() => setDeleteTarget(item)}>{i18n.actions.delete}</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : null,
      }),
    ],
    [canWrite, rotateMutation, styles.mono, styles.scopes],
  )

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={i18n.search}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={i18n.search}
        contentBefore={<SearchRegular />}
        aria-label={i18n.search}
      />
      <Dropdown
        className={styles.statusFilter}
        value={status === "all" ? i18n.filterAll : STATUS_LABEL[status]}
        selectedOptions={[status]}
        onOptionSelect={(_, data) => setStatus((data.optionValue as StatusFilter) ?? "all")}
        aria-label={i18n.filterStatus}
      >
        <Option value="all">{i18n.filterAll}</Option>
        <Option value="active">{i18n.status.active}</Option>
        <Option value="disabled">{i18n.status.disabled}</Option>
      </Dropdown>
      {canWrite ? (
        <Button
          className={styles.spacer}
          appearance="primary"
          icon={<AddRegular />}
          onClick={openCreate}
        >
          {i18n.create}
        </Button>
      ) : null}
    </Toolbar>
  )

  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <DataGridPage<ApiClient>
        queryKey={[...QUERY_KEY, search.trim(), status]}
        fetchPage={(cursor) => fetchClients(cursor, search, status)}
        columns={columns}
        getRowId={(item) => item.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<PlugConnectedRegular />}
            title={i18n.empty.title}
            description={i18n.empty.description}
            action={
              canWrite ? (
                <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                  {i18n.empty.action}
                </Button>
              ) : undefined
            }
          />
        }
      />

      <ClientFormDrawer
        open={formOpen}
        client={editing}
        onClose={() => setFormOpen(false)}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={i18n.delete.title}
        message={i18n.delete.message}
        confirmLabel={i18n.delete.confirm}
        danger
        confirming={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await deleteMutation.mutateAsync(deleteTarget.id)
          setDeleteTarget(null)
        }}
      />

      <Dialog
        open={secret !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setSecret(null)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.secret.title}</DialogTitle>
            <DialogContent>
              <p>{i18n.secret.description}</p>
              <Field label={i18n.secret.clientId}>
                <Input readOnly value={secret?.clientId ?? ""} className={styles.mono} />
              </Field>
              <Field label={i18n.secret.clientSecret}>
                <Input readOnly value={secret?.secret ?? ""} className={styles.mono} />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  if (secret) void navigator.clipboard?.writeText(secret.secret)
                }}
              >
                {i18n.secret.copy}
              </Button>
              <Button appearance="secondary" onClick={() => setSecret(null)}>
                {i18n.secret.close}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
